import React, { useState, useCallback } from "react";
import { useDrag, useDrop } from "react-dnd";
import { FaXmark } from "react-icons/fa6";
import { RiCalendarScheduleLine } from "react-icons/ri";
import { useGetSerachColumnsQuery } from "./dataApiSlice";
import debounce from "lodash/debounce";
import { RxCross2 } from "react-icons/rx";
import { RiArrowRightSLine } from "react-icons/ri";
import { useDispatch } from "react-redux";
import {
  setWorkbookProperty,
  updateQueryItemForHierarchy,
} from "./WorkbookSlice";
import { v4 as uuidv4 } from "uuid";
import ObjectId from "bson-objectid";
import { handleUpdateWorkbook } from "./components/WorkbookComponets/HandleUpdateWorkbook";
import { nanoid } from "@reduxjs/toolkit";
import LoadingIndicator from "../../components/BasicComponents/LoadingIndicator";

const ITEM_TYPE = "HIERARCHY_ITEM"; // Item type for drag-and-drop

function DraggableItem({
  data,
  index,
  handleRemoveItem,
  isInDroppableArea,
  moveItem,
}) {
  // console.log(data);

  const [{ isDragging }, drag] = useDrag({
    type: ITEM_TYPE,
    item: { data, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [{ isOver }, drop] = useDrop({
    accept: ITEM_TYPE,
    hover: (data) => {
      if (data.index !== index) {
        moveItem(data.index, index);
        data.index = index;
      }
    },
  });

  const opacity = isDragging ? 0.5 : 1;

  let displayContent;
  switch (data !== undefined && data.dataType) {
    case "String":
      displayContent = <span className=" text-blue-950">abc</span>;
      break;
    case "Number":
      displayContent = <span className="text-[rgb(90,193,137)]">#</span>;
      break;
    case "Date":
      displayContent = <RiCalendarScheduleLine className=" text-blue-950" />;
      break;
    default:
      displayContent = null;
  }

  return (
    <button
      ref={(node) => drag(drop(node))}
      style={{ opacity }}
      className="flex px-3 items-center justify-start w-full h-7 bg-gray-200 rounded gap-2 font-light cursor-move"
    >
      {displayContent}
      <span className="flex items-center gap-2 truncate w-[75%]">
        {data !== undefined ? data.rename : null}
      </span>
      {isInDroppableArea && (
        <RxCross2
          className="cursor-pointer"
          onClick={() => handleRemoveItem(data._id)}
        />
      )}
    </button>
  );
}

function DroppableArea({ droppedItems, setDroppedItems }) {
  const [{ isOver }, drop] = useDrop({
    accept: ITEM_TYPE,
    drop: (item) => {
      handleDrop(item);
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  const handleDrop = (item) => {
    // console.log(item);
    const requiredItem = { ...item.data, nanoId: nanoid() };
    // console.log(requiredItem);
    const newDroppedItems = [...droppedItems];

    // Check if the item already exists in the droppedItems array
    const existingItemIndex = newDroppedItems.findIndex(
      (droppedItem) => droppedItem.rename === requiredItem.rename
    );

    if (existingItemIndex !== -1) {
      // If the item exists, swap it with the new item
      const temp = newDroppedItems[existingItemIndex];
      newDroppedItems[existingItemIndex] = requiredItem;
      newDroppedItems.push(temp);
    } else {
      // If item is not found, add it to the droppedItems
      setDroppedItems([...newDroppedItems, requiredItem]);
    }
  };

const moveItem = (fromIndex, toIndex) => {
  // console.log(fromIndex);
  // console.log(toIndex);

  // Make sure both indices are defined and not null (0 is allowed)
  const isValidIndex = (index) => index !== null && index !== undefined;

  if (isValidIndex(fromIndex) && isValidIndex(toIndex)) {
    const updatedItems = [...droppedItems];
    const temp = updatedItems[fromIndex];
    updatedItems[fromIndex] = updatedItems[toIndex];
    updatedItems[toIndex] = temp;

    // console.log(updatedItems);
    setDroppedItems(updatedItems);
  }
};


  const handleRemoveItem = (id) => {
    // Remove an item by matching the _id
    const updatedArray = droppedItems.filter((item) => item._id !== id);
    setDroppedItems(updatedArray);
  };

  return (
    <div
      ref={drop}
      className={`flex flex-col items-center justify-start w-1/2 border border-gray-200 rounded overflow-hidden overflow-y-auto h-80 p-2 gap-1 ${
        isOver ? "bg-blue-100" : ""
      }`}
    >
      {droppedItems.map((item, i) => (
        <DraggableItem
          key={i}
          data={item}
          index={i}
          handleRemoveItem={handleRemoveItem}
          isInDroppableArea={true}
          moveItem={moveItem}
        />
      ))}
    </div>
  );
}

export default function HierarchyModal({
  closeModal,
  mergedColumnsArray,
  dataSourceID,
  hierarchyUUID,
  hierarchy,
  workbookId,
  updateWorkbook,
  rowsArray,
  groupByFieldsArray,
  Sheetid,
}) {
  // console.log(dataSourceID);

  const foundHierachy = hierarchy.find(
    (item) => item.hierarchyColumn.uuid === hierarchyUUID
  );
  // console.log(foundHierachy);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  // console.log(debouncedSearchTerm);
  const [droppedItems, setDroppedItems] = useState(
    foundHierachy !== undefined ? foundHierachy.hierarchyArray : []
  );
  // console.log(droppedItems);
  const [hierarchyName, setHierarchyName] = useState(
    foundHierachy !== undefined ? foundHierachy.hierarchyColumn.rename : ""
  );
  // console.log(hierarchyName);

  const dispatch = useDispatch();
  const {
    data: searchedData,
    isSuccess: isSearchSuccess,
    isLoading: isSearchLoading,
    isError: isSearchError,
    error: searchError,
  } = useGetSerachColumnsQuery(
    { dataSourceID, value: debouncedSearchTerm, workbookId },
    { skip: !debouncedSearchTerm }
  );

  const debouncedSetSearchTerm = useCallback(
    debounce((value) => setDebouncedSearchTerm(value), 300),
    []
  );

  const handleSearchColumn = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSetSearchTerm(value);
  };

  const handleHierarchyName = (e) => {
    const name = e.target.value;
    setHierarchyName(name);
  };
  const uuid = uuidv4();
  const _id = new ObjectId().toHexString();

  const handleSave = () => {
    const hierarchyObject = {
      hierarchyColumn: {
        columnName: hierarchyName,
        rename: hierarchyName,
        uuid: foundHierachy ? foundHierachy.hierarchyColumn.uuid : uuid,
        _id,
        dataType: "Hierarchy",
        columnType: "hierarchy",
        hierarchyType: "String",
      },
      dataSourceID,
      hierarchyArray: droppedItems,
    };

    // console.log(hierarchyObject);
    // console.log(hierarchyObject.hierarchyArray);
    const firstItemOfHierarchyArray = hierarchyObject.hierarchyArray[0];
    dispatch(
      updateQueryItemForHierarchy({
        value: firstItemOfHierarchyArray,
      })
    );
    if (hierarchyName === "" || !dataSourceID || droppedItems.length === 0) {
      console.error(
        "hierarchyObject is not defined or some value in it is missing"
      );
    } else {
      // Check if the hierarchy already exists
      const updatedHeirarchyArray = hierarchy.map(
        (item) =>
          item.hierarchyColumn.uuid === hierarchyObject.hierarchyColumn.uuid
            ? hierarchyObject // Update the existing hierarchy object
            : item // Leave other hierarchy objects unchanged
      );

      // If no matching hierarchy is found, add the new one
      if (!foundHierachy) {
        updatedHeirarchyArray.push(hierarchyObject);
      }

      // console.log(updatedHeirarchyArray);

      // Dispatch the updated array to the Redux store
      dispatch(
        setWorkbookProperty({
          itemInArray: false,
          key: "hierarchy",
          value: updatedHeirarchyArray, // Use the updated array
        })
      );
    }
    closeModal();
    handleUpdateWorkbook(updateWorkbook);
  };

  // Use mergedColumnsArray if searchedData is empty
  const items =
    debouncedSearchTerm && searchedData?.length
      ? searchedData
      : mergedColumnsArray;

  // Log the data for debugging
  // console.log("items:", items);

  return (
    <main className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-20">
      <main className="w-full max-w-[30rem] h-auto bg-white shadow-2xl rounded flex flex-col gap-1 min-w-[25rem]">
        <header className="flex bg-gray-50 w-full p-2 font-medium items-center justify-between rounded-t-lg">
          Set Hierarchy
          <button onClick={closeModal}>
            <FaXmark />
          </button>
        </header>

        {/* Hierarchy Name */}
        <section className="flex items-center justify-start w-full gap-2 p-2">
          <label htmlFor="hierarchyName" className="w-40">
            Hierarchy Name
          </label>
          <input
            type="text"
            name="hierarchyName"
            id="hierarchyName"
            placeholder="name will be ..."
            value={hierarchyName}
            className="border border-gray-200 rounded w-full px-2"
            onChange={handleHierarchyName}
          />
        </section>

        <section className="p-2 w-full flex items-center justify-center gap-2">
          {/* Searchable List */}
          <div className="flex flex-col items-center justify-start w-1/2 border border-gray-200 rounded p-2 gap-2 h-80">
            <input
              autoComplete="off"
              type="search"
              placeholder="Search..."
              onChange={handleSearchColumn}
              value={searchTerm}
              className="px-3 w-full rounded h-8 border-2 outline-none"
            />
            <div className="flex flex-col h-full overflow-hidden overflow-y-auto items-start justify-start w-full gap-1">
              {isSearchError ? (
                <div>{searchError?.data?.message || "Error occurred"}</div>
              ) : isSearchLoading ? (
                <LoadingIndicator />
              ) : (
                items.map((data, index) => {
                  // console.log(data);

                  if (
                    data &&
                    data.dataType !== "Date" &&
                    data.dataType !== "Number" &&
                    data.dataType !== "Hierarchy"
                  ) {
                    return (
                      <DraggableItem
                        key={index}
                        data={data}
                        index={index}
                        isInDroppableArea={false}
                        moveItem={() => {}}
                      />
                    );
                  }
                })
              )}
            </div>
          </div>

          {/* Droppable Area */}
          <DroppableArea
            droppedItems={droppedItems}
            setDroppedItems={setDroppedItems}
          />
        </section>

        {/* Preview */}
        <section
          className={`flex w-[97%] ml-2 px-2 items-center justify-start border border-gray-200 ${
            droppedItems.length === 0 ? "h-7" : ""
          }`}
        >
          {droppedItems.map((hie, index) => (
            <button key={index} className={`flex w-auto items-center`}>
              {hie !== undefined && hie.rename}
              {index < droppedItems.length - 1 && <RiArrowRightSLine />}
            </button>
          ))}
        </section>

        <footer className="flex w-full items-center justify-end gap-4 p-2">
          <div className="flex items-center justify-end gap-4">
            <button
              className="border px-2 py-1 tracking-wide"
              onClick={closeModal}
            >
              Close
            </button>
            <button
              className={`border px-2 py-1 tracking-wide   ${
                hierarchyName === "" || droppedItems.length === 0
                  ? "bg-gray-400 text-black"
                  : "bg-blue-950 text-white"
              }`}
              disabled={hierarchyName === "" || droppedItems.length === 0}
              onClick={handleSave}
            >
              Apply
            </button>
          </div>
        </footer>
      </main>
    </main>
  );
}
